import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  BarChart3,
  BellRing,
  Briefcase,
  CalendarDays,
  Clock3,
  FolderKanban,
  LogOut,
  MessageSquare,
  RefreshCw,
  Receipt,
  ScrollText,
  Settings,
  Sparkles,
  Users,
  X,
} from 'lucide-react';
import { api } from './lib/api.js';
import { getConfig } from './lib/runtimeConfig.js';
import { handleAzureRedirect, signInWithAzure } from './lib/msal.js';
import { roleLabel } from './lib/roles.js';
import {
  canOpenClientDetail,
  canOpenProjectDetail,
  canOpenUserDetail,
} from './lib/detailAccess.js';
import { addDays, isoDate, mondayOf, parseIsoDate } from './lib/time.js';
import {
  buildSearch,
  historyMode,
  openedDetailFromList,
  poppedBilling,
  readUrlState,
  withoutReportFilters,
} from './lib/urlState.js';
import { useOutlookCalendar } from './lib/useOutlookCalendar.js';
import { useServerTimer } from './lib/useServerTimer.js';
import { claimSessionState, clearSession } from './lib/sessionState.js';
import { useSessionState } from './lib/useSessionState.js';
import { validIso } from './lib/dateInput.js';
import {
  defaultRange,
  ReportRangeContext,
} from './components/reporting/useReportFilters.js';
import {
  Avatar,
  ConfirmModal,
  NavItem,
  NavSection,
  Portal,
} from './components/ui.jsx';
import { LoadingScreen, Logo } from './components/Brand.jsx';
import { LoginScreen } from './components/LoginScreen.jsx';
import { EntryModal } from './components/EntryModal.jsx';
import { WhatsNewModal } from './components/WhatsNewModal.jsx';
import { TimerBar } from './components/TimerBar.jsx';
import { TooltipLayer } from './components/controls/TooltipLayer.jsx';
import { CalendarView } from './pages/CalendarView.jsx';
import { Timesheet } from './pages/Timesheet.jsx';
import { MyReport } from './pages/MyReport.jsx';
import { Projects, ProjectDetail } from './pages/Projects.jsx';
import { Reports } from './pages/Reports.jsx';
import { Team } from './pages/Team.jsx';
import { ClientDetail, Clients } from './pages/Clients.jsx';
import { SettingsPage } from './pages/SettingsPage.jsx';
import { TimesheetReminders } from './pages/TimesheetReminders.jsx';
import { Trace } from './pages/Trace.jsx';
import { Billing } from './pages/Billing.jsx';
import './styles.css';

// The active top-level page is remembered here so a refresh keeps you where you
// were instead of always dropping back on the calendar. Only the top-level view
// is stored — not detail drill-downs — and it is re-validated against the
// caller's permissions on restore (a page they can no longer open falls back to
// the calendar).
const ACTIVE_VIEW_KEY = 'tt_active_view';

function isViewAllowed(view, data) {
  switch (view) {
    case 'calendar':
    case 'timesheet':
    case 'my-report':
    case 'settings':
      return true;
    case 'clients':
    case 'projects':
    case 'reports':
      return Boolean(data.canSeeManagement);
    case 'team':
      return Boolean(data.canSeeTeam);
    case 'billing':
    case 'reminders':
    case 'trace':
      return Boolean(data.canManageAll);
    default:
      return false;
  }
}

function readStoredView(data) {
  const stored = window.localStorage.getItem(ACTIVE_VIEW_KEY);
  return stored && isViewAllowed(stored, data) ? stored : 'calendar';
}

// A detail URL is valid when the caller can open that entity, even if the
// list view itself is not theirs — e.g. a project manager can open a team
// member's detail from Reports although the Team list (canSeeTeam) is not.
// Mirrors the in-app openUserDetail/openProjectDetail/openClientDetail
// helpers, which gate on the entity, not the page.
function canOpenDetailUrl(view, detailId, data) {
  if (!detailId) return false;
  if (view === 'team') return canOpenUserDetail(data, detailId);
  if (view === 'projects') return canOpenProjectDetail(data, detailId);
  if (view === 'clients') return canOpenClientDetail(data, detailId);
  return false;
}

// The view a URL may restore: the view when the caller may open it or the URL
// points at a detail they can open; the fallback otherwise.
function viewFromUrl(state, data, fallback) {
  if (!state.view) return fallback;
  if (isViewAllowed(state.view, data)) return state.view;
  if (canOpenDetailUrl(state.view, state.detailId, data)) return state.view;
  return fallback;
}

export default function App() {
  const [authed, setAuthed] = useState(null); // null = checking, false = no session, true = logged in
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  async function load() {
    setData(await api('/bootstrap'));
    setAuthed(true);
  }

  async function login(email, password) {
    await api('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    await load();
  }

  // Called with an Azure ID token (from redirect response)
  async function loginWithAzure(idToken) {
    await api('/auth/azure', {
      method: 'POST',
      body: JSON.stringify({ idToken }),
    });
    await load();
  }

  // On every cold load: check for Azure redirect, then try existing session cookie
  React.useEffect(() => {
    handleAzureRedirect()
      .then((idToken) => {
        if (idToken) return loginWithAzure(idToken);
        // No Azure redirect — try to resume an existing session via cookie
        return load().catch(() => {
          setAuthed(false);
        }); // no session → show login
      })
      .catch((err) => {
        setError(err.message);
        setAuthed(false);
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (authed === null) return <LoadingScreen />;
  if (!authed)
    return (
      <LoginScreen
        error={error}
        onLogin={login}
        onAzureLogin={signInWithAzure}
      />
    );
  // Guard on data.me, not just data: AppShell reads it unconditionally (e.g.
  // the sidebar profile), and a malformed/partial bootstrap response should
  // keep showing the loading screen rather than crash the shell.
  if (!data?.me)
    return <LoadingScreen label="Loading Born Digital time tracking..." />;

  return (
    <AppShell
      data={data}
      reload={load}
      onLogout={async () => {
        await api('/auth/logout', { method: 'POST' }).catch(() => {});
        clearSession();
        // The report range and work type in the URL went with the session;
        // the next sign-in on this tab must not read them back.
        const search = withoutReportFilters(window.location.search);
        if (search !== window.location.search)
          window.history.replaceState(
            window.history.state,
            '',
            `${window.location.pathname}${search}${window.location.hash}`,
          );
        setData(null);
        setAuthed(false);
      }}
    />
  );
}

// The calendar week is kept as its Monday, not as an offset from today, so a
// remembered week stays the same week after midnight on a Sunday.
function sanitizeMonday(value, fallback) {
  return validIso(value) || fallback;
}

function AppShell({ data, reload, onLogout }) {
  const [ownerChanged] = useState(() => claimSessionState(data.me.id));
  // Navigation state initializes from the URL when present (deep link,
  // refresh, back/forward restore), then falls back to localStorage and
  // defaults. The Outlook OAuth callback lands on `?outlook=connected`, which
  // opens Settings once; the URL-sync effect below rewrites the URL to its
  // canonical form right away, so the param never survives a reload. A tab
  // taken over from another account ignores that account's report range and
  // work type; the sync effect then writes the defaults back.
  const [initialUrl] = useState(() =>
    readUrlState(
      ownerChanged
        ? withoutReportFilters(window.location.search)
        : window.location.search,
    ),
  );
  const [view, setView] = useState(() => {
    if (new URLSearchParams(window.location.search).has('outlook'))
      return 'settings';
    return viewFromUrl(initialUrl, data, readStoredView(data));
  });
  const [openProject, setOpenProject] = useState(() =>
    initialUrl.view === 'projects' &&
    canOpenProjectDetail(data, initialUrl.detailId)
      ? initialUrl.detailId
      : null,
  );
  const [openClient, setOpenClient] = useState(() =>
    initialUrl.view === 'clients' &&
    canOpenClientDetail(data, initialUrl.detailId)
      ? initialUrl.detailId
      : null,
  );
  const [openUser, setOpenUser] = useState(() =>
    initialUrl.view === 'team' && canOpenUserDetail(data, initialUrl.detailId)
      ? initialUrl.detailId
      : null,
  );
  // The tab of each opened detail, keyed by its view and id. The URL's `tab`
  // seeds the record it came with and Back/Forward re-apply it; a tab picked
  // in the app stays with its record until the page reloads, so a record not
  // opened yet starts on its default tab instead of the last one's.
  const [detailTabs, setDetailTabs] = useState(() =>
    initialUrl.tab
      ? { [`${initialUrl.view}:${initialUrl.detailId}`]: initialUrl.tab }
      : {},
  );
  // The report date range + work type shared by every report screen (Team,
  // Reports, Clients/Projects and their details) via ReportRangeContext — the
  // period picked on a list survives drilling into a detail.
  const [reportFrom, setReportFrom] = useState(
    () => initialUrl.from || defaultRange().from,
  );
  const [reportTo, setReportTo] = useState(
    () => initialUrl.to || defaultRange().to,
  );
  const [reportBilling, setReportBilling] = useState(
    () => initialUrl.billing || 'all',
  );
  const reportRange = useMemo(
    () => ({
      from: reportFrom,
      to: reportTo,
      billing: reportBilling,
      setFrom: setReportFrom,
      setTo: setReportTo,
      setBilling: setReportBilling,
    }),
    [reportFrom, reportTo, reportBilling],
  );
  const [entryModal, setEntryModal] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [deletingEntry, setDeletingEntry] = useState(false);
  const [deleteEntryError, setDeleteEntryError] = useState(null);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [whatsNewOpen, setWhatsNewOpen] = useState(false);
  const thisMonday = isoDate(mondayOf(new Date()));
  const [weekMonday, setWeekMonday] = useSessionState(
    'calendar.week',
    thisMonday,
    sanitizeMonday,
  );
  const weekOffset = Math.round(
    (parseIsoDate(weekMonday) - parseIsoDate(thisMonday)) / (7 * 86400000),
  );
  const setWeekOffset = useCallback(
    (next) =>
      setWeekMonday((monday) => {
        const current = Math.round(
          (parseIsoDate(monday) - parseIsoDate(isoDate(mondayOf(new Date())))) /
            (7 * 86400000),
        );
        const offset = typeof next === 'function' ? next(current) : next;
        return isoDate(addDays(mondayOf(new Date()), offset * 7));
      }),
    [setWeekMonday],
  );
  // The timer lives on the server: a refresh, a closed tab, a logout or a
  // second tab never loses it, so leaving the page needs no guard.
  const {
    timer,
    limits: timerLimits,
    actions: timerActions,
  } = useServerTimer({
    timer: data.activeTimer,
    serverNow: data.timerServerNow,
    limits: data.timerLimits,
  });
  const canManage = data.canSeeManagement;

  const weekStart = isoDate(addDays(mondayOf(new Date()), weekOffset * 7));
  const outlook = useOutlookCalendar(
    data.outlookConnected,
    weekStart,
    view === 'calendar',
  );

  // Remember the current top-level page so a refresh restores it. This captures
  // every path that changes `view` (nav plus the detail-open helpers), which is
  // why it lives here rather than inside nav().
  useEffect(() => {
    window.localStorage.setItem(ACTIVE_VIEW_KEY, view);
  }, [view]);

  const detailId =
    view === 'team'
      ? openUser
      : view === 'projects'
        ? openProject
        : view === 'clients'
          ? openClient
          : null;
  const detailTab = detailId ? detailTabs[`${view}:${detailId}`] || null : null;
  function setDetailTab(tab) {
    const key = `${view}:${detailId}`;
    setDetailTabs((tabs) => ({ ...tabs, [key]: tab }));
  }

  // Mirror navigation state into the URL (?view=...&user=...&from=...&to=...)
  // so refresh, back/forward and shared links work — `historyMode` decides
  // whether a change stacks a history entry, replaces the current one, or
  // walks back (closing a detail that sits on top of its own list entry).
  // Whether the current entry is such a detail is stamped into history.state
  // when it is pushed, so the tracking survives browser Back/Forward. The
  // first run normalizes whatever URL the page loaded with, which also drops
  // the one-shot `?outlook=` param.
  const lastNavRef = useRef(null);
  // Seeded from history.state, not `false`: a reload on a detail keeps the
  // marker in its history entry, and the close-after-refresh must still walk
  // back instead of stacking a duplicate list entry.
  const detailFromListRef = useRef(
    Boolean(window.history.state && window.history.state.detailFromList),
  );
  const keepRangeOnPopRef = useRef(false);
  useEffect(() => {
    const previous = lastNavRef.current;
    const next = { view, detailId: detailId || null };
    const mode = historyMode(previous, next, detailFromListRef.current);
    lastNavRef.current = next;
    if (mode === 'back') {
      // The entry beneath this detail is its own list — walk back to it
      // instead of stacking a duplicate list entry. The popstate handler
      // keeps the current date range while re-applying that entry.
      detailFromListRef.current = false;
      keepRangeOnPopRef.current = true;
      window.history.back();
      return;
    }
    // Recompute the marker only on real screen moves; the first run
    // (previous === null) must keep the value seeded from history.state.
    const movedScreen =
      previous &&
      (previous.view !== next.view || previous.detailId !== next.detailId);
    if (movedScreen)
      detailFromListRef.current = openedDetailFromList(previous, next);
    const search = buildSearch({
      view,
      detailId,
      tab: detailTab,
      from: reportFrom,
      to: reportTo,
      billing: reportBilling,
    });
    if (window.location.search !== search) {
      const url = `${window.location.pathname}${search}${window.location.hash}`;
      if (mode === 'push')
        window.history.pushState(
          detailFromListRef.current ? { detailFromList: true } : null,
          '',
          url,
        );
      else window.history.replaceState(window.history.state, '', url);
    }
  }, [view, detailId, detailTab, reportFrom, reportTo, reportBilling]);

  // Back/forward: re-apply the state the URL encodes. The view and detail id
  // are re-validated against the current bootstrap (a detail the caller can
  // no longer open falls back to its list); a URL without a range keeps the
  // current one, and a pop initiated by closing a detail in-app keeps it too
  // (the range is not an undo stack). The URL is normalized here with
  // replaceState so the sync effect above never mistakes a popped entry for a
  // fresh navigation.
  useEffect(() => {
    function onPopState(event) {
      const keepRange = keepRangeOnPopRef.current;
      keepRangeOnPopRef.current = false;
      const state = readUrlState(window.location.search);
      const nextView = viewFromUrl(state, data, 'calendar');
      const nextUser =
        nextView === 'team' && canOpenUserDetail(data, state.detailId)
          ? state.detailId
          : null;
      const nextProject =
        nextView === 'projects' && canOpenProjectDetail(data, state.detailId)
          ? state.detailId
          : null;
      const nextClient =
        nextView === 'clients' && canOpenClientDetail(data, state.detailId)
          ? state.detailId
          : null;
      const nextDetail = nextUser || nextProject || nextClient;
      const nextFrom = keepRange ? reportFrom : state.from || reportFrom;
      const nextTo = keepRange ? reportTo : state.to || reportTo;
      const nextBilling = poppedBilling(state, reportBilling, keepRange);
      detailFromListRef.current = Boolean(
        event.state && event.state.detailFromList,
      );
      setView(nextView);
      setOpenUser(nextUser);
      setOpenProject(nextProject);
      setOpenClient(nextClient);
      if (nextDetail)
        setDetailTabs((tabs) => ({
          ...tabs,
          [`${nextView}:${nextDetail}`]: state.tab,
        }));
      setReportFrom(nextFrom);
      setReportTo(nextTo);
      setReportBilling(nextBilling);
      const search = buildSearch({
        view: nextView,
        detailId: nextDetail,
        tab: state.tab,
        from: nextFrom,
        to: nextTo,
        billing: nextBilling,
      });
      if (window.location.search !== search)
        window.history.replaceState(
          window.history.state,
          '',
          `${window.location.pathname}${search}${window.location.hash}`,
        );
      lastNavRef.current = { view: nextView, detailId: nextDetail || null };
    }
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [data, reportFrom, reportTo, reportBilling]);

  // Start a timer straight from a calendar event — only possible when no
  // timer is already running (this app tracks one active timer at a time).
  function startTimerFromEvent(description) {
    if (timer) return;
    timerActions.start({ description }).catch(() => {});
  }

  function nav(next) {
    setView(next);
    setOpenProject(null);
    setOpenClient(null);
    setOpenUser(null);
  }

  function openProjectDetail(id) {
    if (!canOpenProjectDetail(data, id)) return;
    setView('projects');
    setOpenClient(null);
    setOpenUser(null);
    setOpenProject(id);
  }

  function openClientDetail(id) {
    if (!canOpenClientDetail(data, id)) return;
    setView('clients');
    setOpenProject(null);
    setOpenUser(null);
    setOpenClient(id);
  }

  function openUserDetail(id) {
    if (!canOpenUserDetail(data, id)) return;
    setView('team');
    setOpenProject(null);
    setOpenClient(null);
    setOpenUser(id);
  }

  async function saveEntry(entry) {
    const payload = {
      projectId: entry.projectId,
      workDate: entry.workDate,
      start: entry.start,
      end: entry.end,
      description: entry.description,
    };
    if (entry.id)
      await api(`/time-entries/${entry.id}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
    else
      await api('/time-entries', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
    setEntryModal(null);
    // The save already succeeded at this point; a failed refresh just means
    // the calendar may show stale data until the next successful reload —
    // it must not be reported as a save failure (that would invite a retry
    // that re-submits an entry that's already saved).
    reload().catch(() => {});
  }

  function requestDeleteEntry(id) {
    setDeleteEntryError(null);
    setConfirmDeleteId(id);
  }

  async function deleteEntry(id) {
    await api(`/time-entries/${id}`, { method: 'DELETE' });
    setEntryModal(null);
    // The delete already succeeded at this point; a failed refresh must not
    // be reported as a delete failure — that would invite a retry that
    // re-deletes an id that's already gone (a 404).
    reload().catch(() => {});
  }

  return (
    <ReportRangeContext.Provider value={reportRange}>
      <div className="shell">
        <header className="app-bar">
          <div className="app-brand">
            <Logo height={24} />
            <span className="app-bar-divider" />
            <div className="app-title">
              <strong>Time Tracker</strong>
              <small className="app-version" data-tooltip="Running version">
                {getConfig('APP_VERSION', 'dev')}
              </small>
            </div>
          </div>
          <span className="app-bar-divider" />
          <TimerBar
            data={data}
            timer={timer}
            limits={timerLimits}
            actions={timerActions}
            onSaved={() => reload().catch(() => {})}
          />
          <div className="app-bar-actions">
            <button
              type="button"
              className="icon-button"
              onClick={() => setWhatsNewOpen(true)}
              aria-label="What's new"
              data-tooltip="What's new"
            >
              <Sparkles size={18} />
            </button>
            <button
              type="button"
              className="icon-button"
              onClick={() => setFeedbackOpen(true)}
              aria-label="Feedback"
              data-tooltip="Feedback"
            >
              <MessageSquare size={18} />
            </button>
            <span className="app-bar-divider" />
            <Avatar user={data.me} variant="chrome" size={32} />
          </div>
        </header>
        <div className="shell-body">
          <Sidebar
            data={data}
            view={view}
            canManage={canManage}
            nav={nav}
            onLogout={onLogout}
            outlookLastUpdated={outlook.lastUpdated}
            outlookRefreshing={outlook.refreshing}
            onOutlookRefresh={
              view === 'calendar'
                ? () => outlook.refresh().catch(() => {})
                : undefined
            }
          />
          <main className="main">
            <section className="content">
              <RouteView
                view={view}
                data={data}
                reload={reload}
                timer={timer}
                saveEntry={saveEntry}
                deleteEntry={requestDeleteEntry}
                openProject={openProject}
                setOpenProject={setOpenProject}
                openClient={openClient}
                setOpenClient={setOpenClient}
                openUser={openUser}
                setOpenUser={setOpenUser}
                detailTab={detailTab}
                setDetailTab={setDetailTab}
                openProjectDetail={openProjectDetail}
                openClientDetail={openClientDetail}
                openUserDetail={openUserDetail}
                setEntryModal={setEntryModal}
                openSettings={() => nav('settings')}
                weekOffset={weekOffset}
                setWeekOffset={setWeekOffset}
                outlook={outlook}
                onStartTimerFromEvent={startTimerFromEvent}
              />
            </section>
          </main>
        </div>
        {entryModal && (
          <EntryModal
            initial={entryModal}
            data={data}
            onSave={saveEntry}
            onDelete={requestDeleteEntry}
            onClose={() => setEntryModal(null)}
          />
        )}
        {confirmDeleteId && (
          <ConfirmModal
            title="Delete time entry"
            message="This time entry will be permanently deleted. This action cannot be undone."
            confirmLabel="Delete"
            loading={deletingEntry}
            error={deleteEntryError}
            onConfirm={async () => {
              setDeletingEntry(true);
              try {
                await deleteEntry(confirmDeleteId);
                setConfirmDeleteId(null);
              } catch (err) {
                setDeleteEntryError(
                  err.message || 'Could not delete. Please try again.',
                );
              } finally {
                setDeletingEntry(false);
              }
            }}
            onClose={() => setConfirmDeleteId(null)}
          />
        )}
        {feedbackOpen && (
          <FeedbackModal
            onClose={() => setFeedbackOpen(false)}
            onSaved={() => setFeedbackOpen(false)}
          />
        )}
        {whatsNewOpen && (
          <WhatsNewModal onClose={() => setWhatsNewOpen(false)} />
        )}
        <TooltipLayer />
      </div>
    </ReportRangeContext.Provider>
  );
}

function Sidebar({
  data,
  view,
  canManage,
  nav,
  onLogout,
  outlookLastUpdated,
  outlookRefreshing,
  onOutlookRefresh,
}) {
  return (
    <aside className="sidebar">
      <NavSection title="My work">
        <NavItem
          icon={<CalendarDays />}
          label="My calendar"
          active={view === 'calendar'}
          onClick={() => nav('calendar')}
        />
        <NavItem
          icon={<Clock3 />}
          label="Timesheet"
          active={view === 'timesheet'}
          onClick={() => nav('timesheet')}
        />
        <NavItem
          icon={<BarChart3 />}
          label="My report"
          active={view === 'my-report'}
          onClick={() => nav('my-report')}
        />
      </NavSection>
      <NavSection title="Management">
        <NavItem
          icon={<Briefcase />}
          label="Clients"
          active={view === 'clients'}
          muted={!canManage}
          onClick={() => canManage && nav('clients')}
        />
        <NavItem
          icon={<FolderKanban />}
          label="Projects"
          active={view === 'projects'}
          muted={!canManage}
          onClick={() => canManage && nav('projects')}
        />
        <NavItem
          icon={<Users />}
          label="Team"
          active={view === 'team'}
          muted={!data.canSeeTeam}
          onClick={() => data.canSeeTeam && nav('team')}
        />
        <NavItem
          icon={<BarChart3 />}
          label="Reports"
          active={view === 'reports'}
          muted={!canManage}
          onClick={() => canManage && nav('reports')}
        />
        {data.canManageAll && (
          <NavItem
            icon={<Receipt />}
            label="Billing"
            active={view === 'billing'}
            onClick={() => nav('billing')}
          />
        )}
        {data.canManageAll && (
          <NavItem
            icon={<BellRing />}
            label="Reminders"
            active={view === 'reminders'}
            onClick={() => nav('reminders')}
          />
        )}
        <NavItem
          icon={<ScrollText />}
          label="Trace"
          active={view === 'trace'}
          muted={!data.canManageAll}
          onClick={() => data.canManageAll && nav('trace')}
        />
      </NavSection>
      <div className="sidebar-secondary-actions">
        {data.outlookConnected && (
          <div
            className="sidebar-outlook-status"
            data-tooltip={`Connected to Outlook${
              data.outlookAccountEmail ? ` as ${data.outlookAccountEmail}` : ''
            }`}
          >
            <span className="sidebar-outlook-label">
              <span className="outlook-status-dot" />
              <span className="truncate">
                {outlookLastUpdated
                  ? `Outlook synced · ${new Date(
                      outlookLastUpdated,
                    ).toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}`
                  : 'Outlook connected'}
              </span>
            </span>
            {onOutlookRefresh && (
              <button
                type="button"
                className="icon-button"
                disabled={outlookRefreshing}
                onClick={onOutlookRefresh}
                aria-label="Refresh Outlook events"
                data-tooltip="Refresh Outlook events"
              >
                <RefreshCw
                  size={13}
                  className={outlookRefreshing ? 'spin' : ''}
                />
              </button>
            )}
          </div>
        )}
      </div>
      <div className="sidebar-footer">
        <Avatar user={data.me} variant="chrome" size={40} />
        <div>
          <strong>{data.me.name}</strong>
          <span>{roleLabel(data.me.role)}</span>
        </div>
        <div className="sidebar-actions">
          <button
            className="icon-button"
            onClick={() => nav('settings')}
            aria-label="Settings"
            data-tooltip="Settings"
          >
            <Settings size={16} />
          </button>
          <button
            className="icon-button"
            onClick={onLogout}
            aria-label="Log out"
            data-tooltip="Log out"
          >
            <LogOut size={16} />
          </button>
        </div>
      </div>
    </aside>
  );
}

function FeedbackModal({ onClose, onSaved }) {
  const [message, setMessage] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const trimmed = message.trim();

  async function submit(event) {
    event.preventDefault();
    if (!trimmed || saving) return;
    setSaving(true);
    setError('');
    try {
      await api('/feedback', {
        method: 'POST',
        body: JSON.stringify({ message: trimmed }),
      });
      onSaved();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Portal>
      <div className="modal-backdrop" onMouseDown={onClose}>
        <form
          className="modal feedback-modal"
          onSubmit={submit}
          onMouseDown={(event) => event.stopPropagation()}
        >
          <header>
            <h2>Feedback</h2>
            <button
              type="button"
              className="icon-button"
              onClick={onClose}
              aria-label="Close"
            >
              <X size={20} />
            </button>
          </header>
          <div className="form-grid">
            <label>
              Message
              <textarea
                autoFocus
                value={message}
                onChange={(event) => setMessage(event.target.value)}
                placeholder="Write feedback..."
                maxLength={5000}
              />
            </label>
            {error && <div className="error">{error}</div>}
            <div className="feedback-actions">
              <span>{trimmed.length}/5000</span>
              <button type="button" className="secondary" onClick={onClose}>
                Cancel
              </button>
              <button
                type="submit"
                className="primary"
                disabled={!trimmed || saving}
              >
                {saving ? 'Sending...' : 'Send'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </Portal>
  );
}

function RouteView({
  view,
  data,
  reload,
  timer,
  saveEntry,
  deleteEntry,
  openProject,
  setOpenProject,
  openClient,
  setOpenClient,
  openUser,
  setOpenUser,
  detailTab,
  setDetailTab,
  openProjectDetail,
  openClientDetail,
  openUserDetail,
  setEntryModal,
  openSettings,
  weekOffset,
  setWeekOffset,
  outlook,
  onStartTimerFromEvent,
}) {
  if (view === 'calendar')
    return (
      <CalendarView
        data={data}
        reload={reload}
        timer={timer}
        openEntry={setEntryModal}
        openQuickAdd={setEntryModal}
        onEntryChange={saveEntry}
        onEntryDelete={deleteEntry}
        onOpenSettings={openSettings}
        weekOffset={weekOffset}
        setWeekOffset={setWeekOffset}
        outlook={outlook}
        onStartTimerFromEvent={onStartTimerFromEvent}
      />
    );
  if (view === 'timesheet')
    return (
      <Timesheet
        data={data}
        openEntry={setEntryModal}
        openQuickAdd={setEntryModal}
      />
    );
  if (view === 'my-report')
    return (
      <MyReport
        data={data}
        onOpenClient={openClientDetail}
        onOpenProject={openProjectDetail}
      />
    );
  if (view === 'projects' && openProject)
    return (
      <ProjectDetail
        projectId={openProject}
        data={data}
        reload={reload}
        tab={detailTab}
        onTabChange={setDetailTab}
        onBack={() => setOpenProject(null)}
      />
    );
  if (view === 'projects')
    return <Projects data={data} onOpen={setOpenProject} onCreated={reload} />;
  if (view === 'reports')
    return (
      <Reports
        data={data}
        onOpenClient={openClientDetail}
        onOpenProject={openProjectDetail}
        onOpenUser={openUserDetail}
      />
    );
  if (view === 'team')
    return (
      <Team
        data={data}
        reload={reload}
        openUser={openUser}
        setOpenUser={setOpenUser}
      />
    );
  if (view === 'clients' && openClient)
    return (
      <ClientDetail
        clientId={openClient}
        data={data}
        reload={reload}
        tab={detailTab}
        onTabChange={setDetailTab}
        onBack={() => setOpenClient(null)}
        onOpenProject={openProjectDetail}
      />
    );
  if (view === 'clients')
    return <Clients data={data} reload={reload} onOpen={setOpenClient} />;
  if (view === 'trace') return <Trace />;
  if (view === 'reminders') return <TimesheetReminders users={data.users} />;
  if (view === 'billing')
    return (
      <Billing
        clients={[...(data.clients || [])]
          .filter((c) => c.active !== false)
          .sort((a, b) => a.name.localeCompare(b.name))}
        onClientsChanged={reload}
      />
    );
  if (view === 'settings') {
    const outlookNeedsReconnect =
      data.outlookNeedsReconnect || outlook.status === 'needs_reconnect';
    return (
      <SettingsPage
        me={data.me}
        outlookConfigured={data.outlookConfigured}
        outlookConnected={data.outlookConnected && !outlookNeedsReconnect}
        outlookNeedsReconnect={outlookNeedsReconnect}
        outlookAccountEmail={data.outlookAccountEmail}
        mcpConfigured={data.mcpConfigured}
        mcpConnections={data.mcpConnections}
        reload={reload}
      />
    );
  }
  return null;
}
